import {parseISO} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {always, isNil, isNotNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {PaymentInfoRequestBody, useInvoicingDocumentRecalculateMutation} from '@dms/api/finance';
import {
  CorrectiveTaxDocumentRequestBody,
  useGetBusinessCaseQuery,
  useGetBusinessCaseSeriesDefinitionListQuery,
  useGetOrderQuery,
} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {useGetCurrentBranch} from '@dms/shared';

import {useQueryState, useRequiredParams} from 'shared/web';

import {PERCENTAGE_SIGN} from '../../../constants';
import {useAccountingCalculationCache} from '../../../context/useAccountingCalculationCache';
import {AccountingDocumentFormValues, InvoiceItem} from '../../../types';
import {createCorrectiveTaxDocumentSchema} from '../../../utils/createCorrectiveTaxDocumentSchema';
import {getInvoicingDocumentType} from '../utils/getInvoicingDocumentType';

export const useGetCorrectiveTaxForBCDocumentBody = () => {
  const formatDateTime = useDateTimeFormatter();

  const [invoiceType] = useQueryState('invoiceType');
  const [orderId] = useQueryState('orderId');
  const [checkoutId] = useQueryState('checkoutId');
  const [businessCaseId] = useQueryState('businessCaseId');

  const {id} = useRequiredParams();

  const {data: tenant} = useGetTenantQuery();
  const {activeBranchId} = useGetCurrentBranch();
  const invoicingDocumentType = getInvoicingDocumentType(invoiceType);
  const {data: documentSeries} = useGetBusinessCaseSeriesDefinitionListQuery();
  const {data: order} = useGetOrderQuery(
    {orderId: orderId ?? '', checkoutId: checkoutId ?? ''},
    {skip: isNil(orderId) || isNil(checkoutId)}
  );

  const {accountingDocumentCalculationCacheKey} = useAccountingCalculationCache();
  const [_, {data: summary}] = useInvoicingDocumentRecalculateMutation({
    fixedCacheKey: accountingDocumentCalculationCacheKey,
  });

  const currency = tenant?.currency as string;

  const activeDefinition =
    documentSeries?.find((item) => item.branchId === activeBranchId) ??
    documentSeries?.find((item) => item.systemDefault) ??
    null;

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: businessCaseId ?? ''},
    {skip: isNil(businessCaseId)}
  );

  const isBrokerageBusinessCase = isNotNil(businessCase?.brokerageBusinessCase);

  const documentSeriesId = match([order?.orderDiscriminator, isBrokerageBusinessCase])
    .with(['SALE', false], always(activeDefinition?.correctiveTaxDocumentSeriesId))
    .with(['SALE', true], always(activeDefinition?.saleBrokerageCorrectiveTaxDocumentSeriesId))
    .with(
      ['PURCHASE_BROKERAGE_SALE', Pattern.boolean],
      ['PURCHASE_BROKERAGE_FEES', Pattern.boolean],
      always(activeDefinition?.brokerageFeesCorrectiveTaxDocumentSeriesId)
    )
    .otherwise(always(activeDefinition?.correctiveTaxDocumentSeriesId));

  const getRequestBody = (
    formData: AccountingDocumentFormValues
  ): CorrectiveTaxDocumentRequestBody => {
    const parsedResult = createCorrectiveTaxDocumentSchema.safeParse(formData);
    if (parsedResult.error || isNil(documentSeriesId)) {
      // TYPE CHECK: there should be always present at least system default series,
      throw new Error('Document series not found');
    }
    const parsedData = parsedResult.data;

    const items = formData.invoiceItems.map((item: InvoiceItem) => {
      const discount =
        summary?.items.find(
          (summaryItem) => summaryItem.itemId === item.itemId && summaryItem.type === 'discount'
        )?.discount ?? null;

      return {
        currency,
        description: item.description,
        isUnitPriceWithVat: item.isUnitPriceWithVat,
        itemId: item.itemId,
        pricePerUnit: item.pricePerUnit?.toString() ?? '0',
        quantity: item.unit === PERCENTAGE_SIGN ? '1' : (item.quantity?.toString() ?? '0'),
        type: item.type,
        unit: item.unit,
        correctedItemId: item.itemId,
        vatType: item.vatType,
        discount: item.type === 'discount' ? discount : null,
        relatedItemId: item.relatedItemId,
      };
    });

    return {
      dateOfReception: parsedData.dateOfReception ?? '',
      behindItemsNote: parsedData.footerNote ?? null,
      customFields: parsedData.customFieldsPayload || [],
      dateOfTaxableSupply: parsedData.dateOfTaxableSupply,
      description: i18n.t('entity.correctiveTaxDocument.labels.descriptionContent', {
        invoiceNumber: parsedData.number,
        issueDate: formatDateTime('dateShort', parseISO(parsedData.issueDate)),
      }),
      documentSeriesId,
      dueDate: parsedData.dueDate,
      internalNote: parsedData.internalNote ?? null,
      issueDate: parsedData.issueDate,
      paymentInfo: parsedData.paymentInfo as PaymentInfoRequestBody,
      additionalCustomer:
        isNotNil(parsedData.additionalCustomer?.customer.id) &&
        isFeatureEnabled(featureFlags.ACCOUNTING_ADDITIONAL_CUSTOMER)
          ? {
              contractInformationId: parsedData.additionalCustomer.contractInformationId,
              customerId: parsedData.additionalCustomer.customer.id,
              type: parsedData.additionalCustomer.type,
            }
          : null,
      reason: parsedData.reason ?? '',
      invoicingDocumentType,
      invoicingDocumentId: id,
      items,
      exchangeRateRatio:
        parsedData.isExchangeRateRatioEnabled &&
        parsedData.exchangeRateRatio &&
        parsedData.exchangeRateRatio.amount &&
        parsedData.exchangeRateRatio.currency &&
        parsedData.exchangeRateRatio.ratio
          ? {
              amount: Number(parsedData.exchangeRateRatio.amount) as 1 | 100 | 1000,
              currency: parsedData.exchangeRateRatio.currency,
              ratio: parsedData.exchangeRateRatio.ratio.toString(),
            }
          : null,
    };
  };

  return [getRequestBody];
};
